import { SIGNOFF_ROLE_LABELS } from 'pages/reporting/shared/approval/approvalRules'
import {
  PDFBool,
  PDFDocument,
  PDFFont,
  PDFImage,
  PDFName,
  PDFPage,
  rgb,
} from 'pdf-lib'
import { SignoffRole } from 'types'

export const CERTIFICATE_SLOT_COUNT = 3

export interface CertificateSigner {
  role: SignoffRole
  signerName: string
  signerEmail: string
  qualifications?: string
  methodDescription: string
  signedAt: string
  sourceSha256: string
}

const STATEMENT =
  'Signed electronically in Cantilever. Each signer was authenticated and chose to sign this document. Signature images exist only in this PDF. The SHA-256 fingerprint beside each signature identifies the exact document bytes that signer approved. This page is retained for internal audit and omitted from the published copy.'

const PAGE = { width: 612, height: 792, margin: 54 }
const SLOT = { firstY: 622, height: 190, imageWidth: 220, imageHeight: 66 }
const INK = rgb(0.07, 0.09, 0.15)
const MUTED = rgb(0.4, 0.42, 0.48)
const RULE = rgb(0.8, 0.82, 0.86)
/** Private page-dictionary key so the page can be found again regardless of position. */
const CERTIFICATE_PAGE_KEY = PDFName.of('GeckoESignCertificate')

export const isCertificatePage = (page: PDFPage) =>
  page.node.has(CERTIFICATE_PAGE_KEY)

/**
 * Appends one signer to the "Electronic Signature Certificate" audit page,
 * creating the page on first use. Holds `CERTIFICATE_SLOT_COUNT` signers.
 */
export function fillCertificateSlot(
  pdfDoc: PDFDocument,
  image: PDFImage,
  signer: CertificateSigner,
  slotIndex: number,
  fonts: { regular: PDFFont; bold: PDFFont }
) {
  if (slotIndex < 0 || slotIndex >= CERTIFICATE_SLOT_COUNT) {
    throw new Error(
      `Certificate page holds ${CERTIFICATE_SLOT_COUNT} signatures`
    )
  }
  const page =
    pdfDoc.getPages().find(isCertificatePage) ??
    createCertificatePage(pdfDoc, fonts)

  const top = SLOT.firstY - slotIndex * SLOT.height
  const textX = PAGE.margin + SLOT.imageWidth + 24
  page.drawLine({
    start: { x: PAGE.margin, y: top + 12 },
    end: { x: PAGE.width - PAGE.margin, y: top + 12 },
    thickness: 0.5,
    color: RULE,
  })
  page.drawText(SIGNOFF_ROLE_LABELS[signer.role], {
    x: PAGE.margin,
    y: top - 6,
    size: 12,
    font: fonts.bold,
    color: INK,
  })
  const scaled = image.scaleToFit(SLOT.imageWidth, SLOT.imageHeight)
  page.drawImage(image, {
    x: PAGE.margin,
    y: top - 24 - scaled.height,
    width: scaled.width,
    height: scaled.height,
  })

  const lines = [
    `Signed by ${signer.signerName} (${signer.signerEmail})`,
    signer.qualifications && `Qualifications: ${signer.qualifications}`,
    `Signed at ${signer.signedAt.replace('T', ' ').slice(0, 19)} UTC`,
    signer.methodDescription,
    'Document SHA-256 before signing:',
  ].filter((line): line is string => !!line)
  let y = top - 10
  for (const line of lines) {
    y = drawWrapped(page, line, textX, y, 9, fonts.regular, INK)
  }
  page.drawText(signer.sourceSha256, {
    x: textX,
    y,
    size: 7.5,
    font: fonts.regular,
    color: MUTED,
  })
}

function createCertificatePage(
  pdfDoc: PDFDocument,
  fonts: { regular: PDFFont; bold: PDFFont }
) {
  const page = pdfDoc.addPage([PAGE.width, PAGE.height])
  page.node.set(CERTIFICATE_PAGE_KEY, PDFBool.True)
  page.drawText('Electronic Signature Certificate', {
    x: PAGE.margin,
    y: PAGE.height - 72,
    size: 18,
    font: fonts.bold,
    color: INK,
  })
  drawWrapped(
    page,
    STATEMENT,
    PAGE.margin,
    PAGE.height - 96,
    9,
    fonts.regular,
    MUTED
  )
  return page
}

/** Draws word-wrapped text and returns the y position of the next free line. */
function drawWrapped(
  page: PDFPage,
  text: string,
  x: number,
  startY: number,
  size: number,
  font: PDFFont,
  color: ReturnType<typeof rgb>
): number {
  const maxWidth = PAGE.width - PAGE.margin - x
  const lines: string[] = []
  let current = ''
  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word
    if (!current || font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  }
  lines.push(current)

  let y = startY
  for (const line of lines) {
    page.drawText(line, { x, y, size, font, color })
    y -= size + 3
  }
  return y
}
